<script setup>
import { ref } from 'vue';
import Modal from './Modal.vue';
import PrimaryButton from './PrimaryButton.vue';
import { useStore } from 'vuex';
import { dispatchNotification } from './Notification';

const emit = defineEmits(['closes', 'created']);
const props = defineProps(['companyId']);

const store = useStore();
const csrfToken = store.state.csrfToken;

const initialFormState = {
    company_id: props.companyId,
    title: '',
    type: '',
    industry: '',
    start: '',
    startToday: false,
    end: '',
    endless: false,
    description: '',
    requirements: '',
    errors: '',
    processing: false,
};

const form = ref({ ...initialFormState });

const resetForm = () => {
    Object.assign(form.value, initialFormState);
}

const submit = () => {
    form.value.processing = true;

    fetch('/api/advertisements', {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(form.value)
    }).then(async (response) => {
        const advertisement = await response.json();
        form.value.processing = false;

        if (response.status === 422) {
            form.value.errors = json.errors;
        } else if (response.status === 201) {
            dispatchNotification({ title: 'Aktion erfolgreich', content: 'Das Stellenangebot wurde erfolgreich angelegt.', type: 'success', duration: 5000 });
            resetForm();
            emit('created', advertisement);

        } else {
            dispatchNotification({ title: 'Es ist ein Fehler aufgetreten', content: 'Fehlercode: ' + response.status, type: 'error', duration: 5000 });
        }
    }).catch((error) => {
        console.log(error);
    });
};

</script>

<template>
    <Modal :show="true" @closes="emit('closes')" max-width="3xl">
        <template #headers>
            <h3 class="tw-text-base tw-font-semibold tw-leading-6 tw-text-gray-900 tw-mb-0">Stellenangebot erstellen
            </h3>
            <p class="tw-mt-1 tw-text-sm tw-text-gray-500 tw-mb-0">Fügen Sie eine neues Stellenangebot hinzu.</p>
        </template>

        <form @submit.prevent="submit" class="tw-space-y-3 tw-px-6 tw-py-5">
            <div class="tw-grid tw-grid-cols-1 tw-gap-x-8 tw-gap-y-10 tw-pb-3 md:tw-grid-cols-3 border-gray-900/10">
                <div class="tw-bg-gray-100 tw-p-5 tw-rounded-lg">
                    <h2 class="tw-text-base tw-font-semibold tw-leading-7 tw-text-gray-900">Allgemeine
                        Informationen</h2>
                    <p class="tw-mt-1 tw-text-sm tw-leading-6 tw-text-gray-600">Geben Sie die allgemeinen
                        Informationen des Stellenangebots ein.</p>
                </div>

                <div
                    class="tw-grid tw-max-w-3xl tw-grid-cols-1 tw-gap-x-6 tw-gap-y-4 sm:tw-grid-cols-6 md:tw-col-span-2 tw-py-5">

                    <div class="sm:tw-col-span-6">
                        <label for="job-request-title">Berufsbezeichnung</label>

                        <div class="input-group">
                            <input v-model="form.title" type="text" id="job-request-title"
                                placeholder="Berufsbezeichnung" class="form-control">
                            <div class="input-group-append">
                                <span class="input-group-text">(m/w/d)</span>
                            </div>
                        </div>
                    </div>

                    <div class="sm:tw-col-span-3">
                        <label for="job-advertisement-type">Typ</label>
                        <select v-model="form.type" id="job-advertisement-type" class="custom-select" required>
                            <option value="">Auswählen...</option>
                            <option value="Vollzeit-Job">Vollzeit-Job</option>
                            <option value="Teilzeit-Job">Teilzeit-Job</option>
                            <option value="Vollzeit/Teilzeit-Job">Vollzeit/Teilzeit-Job</option>
                            <option value="Mini/Midi-Job">Mini/Midi-Job</option>
                            <option value="Ausbildung">Ausbildung</option>
                            <option value="Duales Studium">Duales Studium</option>
                        </select>
                    </div>

                    <div class="sm:tw-col-span-3">
                        <label for="job-advertisement-industry">Branche</label>
                        <select v-model="form.industry" id="job-advertisement-industry" class="custom-select" required>
                            <option value="">Auswählen...</option>
                            <option value="Agrar, Chemie, Rohstoffe">Agrar, Chemie, Rohstoffe</option>
                            <option value="IT und Telekommunikation">IT und Telekommunikation</option>
                            <option value="Sport und Freizeit">Sport und Freizeit</option>
                            <option value="Tourismus und Gastronomie">Tourismus und Gastronomie</option>
                            <option value="Bauen und Wohnen">Bauen und Wohnen</option>
                            <option value="Handel und Handwerk">Handel und Handwerk</option>
                            <option value="Energie und Umwelt">Energie und Umwelt</option>
                            <option value="Finanzen und Immobilien">Finanzen und Immobilien</option>
                            <option value="Medien und Marketing">Medien und Marketing</option>
                            <option value="Technik und Industrie">Technik und Industrie</option>
                            <option value="Gesundheit und Soziales">Gesundheit und Soziales</option>
                            <option value="Verwaltung und Öffentliche Hand">Verwaltung und Öffentliche Hand</option>
                            <option value="Bildung">Bildung</option>
                            <option value="Auto und Mobilität">Auto und Mobilität</option>
                            <option value="Textil">Textil</option>
                        </select>
                    </div>

                    <div class="sm:tw-col-span-3">
                        <label for="job-advertisement-start">Beginn</label>
                        <input v-model="form.start" id="job-advertisement-start" type="month" class="form-control"
                            :disabled="form.startToday" placeholder="YYYY-MM">

                        <div class="tw-text-right tw-mt-2">
                            <input v-model="form.startToday" class="form-check-input" type="checkbox"
                                id="job-advertisement-start-today">
                            <label class="form-check-label" for="job-advertisement-start-today">
                                Sofort
                            </label>
                        </div>
                    </div>

                    <div class="sm:tw-col-span-3">
                        <label for="job-advertisement-duration">Dauer</label>
                        <input v-model="form.end" id="job-advertisement-start" type="month" class="form-control"
                            :disabled="form.endless" placeholder="YYYY-MM">

                        <div class="tw-text-right tw-mt-2">
                            <input v-model="form.endless" class="form-check-input" type="checkbox"
                                id="job-advertisement-duration-endless">
                            <label class="form-check-label" for="job-advertisement-duration-endless">
                                Unbefristet
                            </label>
                        </div>
                    </div>

                </div>
            </div>

            <div class="tw-grid tw-grid-cols-1 tw-gap-x-8 tw-gap-y-10 tw-pb-3 md:tw-grid-cols-3 border-gray-900/10">
                <div class="tw-bg-gray-100 tw-p-5 tw-rounded-lg">
                    <h2 class="tw-text-base tw-font-semibold tw-leading-7 tw-text-gray-900">Weitere Details</h2>
                    <p class="tw-mt-1 tw-text-sm tw-leading-6 tw-text-gray-600">Geben Sie die weiteren Details des
                        Stellenangebots ein.</p>
                </div>

                <div
                    class="tw-grid tw-max-w-3xl tw-grid-cols-1 tw-gap-x-6 tw-gap-y-4 sm:tw-grid-cols-6 md:tw-col-span-2 tw-py-5">
                    <div class="sm:tw-col-span-6">
                        <div class="form-group">
                            <label for="job-advertisement-description">Das ist deine Aufgabe (max. 300 Zeichen)</label>
                            <textarea v-model="form.description" maxlength="300" id="job-advertisement-description"
                                class="form-control" placeholder="Das ist deine Aufgabe" rows="4" required></textarea>
                        </div>

                        <div class="form-group">
                            <label for="job-advertisement-requirements">Das bringst du mit (max. 300 Zeichen)</label>
                            <textarea v-model="form.requirements" maxlength="300" id="job-advertisement-requirements"
                                class="form-control" placeholder="Das bringst du mit" rows="4" required></textarea>
                        </div>
                    </div>

                </div>
            </div>
            <div class="tw-flex tw-items-center tw-justify-end">
                <PrimaryButton class="!tw-w-auto" :loading="form.processing">
                    Speichern
                </PrimaryButton>
            </div>

        </form>


    </Modal>
</template>
